import { create } from 'zustand';
import { RequirementFrontendController } from '../controllers/requirementFrontendController';
import { RequirementProcedureTemplateAssignmentFrontendController } from '../controllers/requirementProcedureTemplateAssignmentController';
import { RequirementTestMethodAssignmentFrontendController } from '../controllers/requirementTestMethodAssignmentController';
import { RequirementMilestoneAssignmentFrontendController } from '../controllers/requirementMilestoneAssignmentController';
import { RequirementSampleMaturityAssignmentFrontendController } from '../controllers/requirementSampleMaturityAssignmentController';
import { RequirementParams, RequirementSubjectType } from '@/backend/access/requirement/types';
import { RequirementProcedureTemplateAssignmentParams } from '@/backend/access/requirementProcedureTemplateAssignment/types';
import { RequirementTestMethodAssignmentParams, TestMethod } from '@/backend/access/requirementTestMethodAssignment/types';
import { RequirementMilestoneAssignmentParams } from '@/backend/access/requirementMilestoneAssignment/types';
import { RequirementSampleMaturityAssignmentParams } from '@/backend/access/requirementSampleMaturityAssignment/types';
import { CreateRequirementRequest } from '@/backend/express/controllers/requirements/requirementsController';
import { RequirementMilestoneAssignment } from '@/backend/models/requirementMilestoneAssignment';
import { RequirementSampleMaturityAssignment } from '@/backend/models/requirementSampleMaturityAssignment';
import { Requirement } from '@/backend/models/requirement';
import { ProjectFrontendController } from '../controllers/projectFrontendController';
import { VerificationCriteriaFrontendController } from '../controllers/verificationCriteriaController';
import { VerificationCriteriaTaskController } from '../controllers/verificationCriteriaTaskController';
import { VerificationCriteriaStatus } from '@/backend/access/verificationCriteria/types';
import {
  VerificationCriteriaTaskCreateInput,
  VerificationCriteriaTaskUpdateInput,
} from '@/backend/access/verificationCriteriaTask/types';
import { CreateVerificationCriteriaRequest, UpdateVerificationCriteriaRequest } from '@/backend/express/controllers/verificationCriteria/verificationCriteriaController';
import { VerificationCriteria } from '@/backend/models/verificationCriteria';
import { VerificationCriteriaTask } from '@/backend/models/verificationCriteriaTask';

const STATUS_SORT_ORDER: Record<VerificationCriteriaStatus, number> = {
  [VerificationCriteriaStatus.MISSING_EVIDENCE]: 0,
  [VerificationCriteriaStatus.PENDING]: 1,
  [VerificationCriteriaStatus.REJECTED]: 2,
  [VerificationCriteriaStatus.ACCEPTED]: 3,
};

interface RequirementsStore {
  requirements: Requirement[];
  organizationRequirements: RequirementParams[];
  assignmentsByRequirement: Record<string, RequirementProcedureTemplateAssignmentParams[]>;
  testMethodAssignmentsByRequirement: Record<string, RequirementTestMethodAssignmentParams[]>;
  requirmentMilestoneAssignments: RequirementMilestoneAssignment[];
  requirementSampleMaturityAssignments: RequirementSampleMaturityAssignment[];
  verificationCriteria: VerificationCriteria[];
  verificationCriteriaTasks: VerificationCriteriaTask[];
  setRequirements: (requirements: RequirementParams[]) => void;
  setOrganizationRequirements: (requirements: RequirementParams[]) => void;
  setAssignmentsForRequirement: (requirementId: string, assignments: RequirementProcedureTemplateAssignmentParams[]) => void;
  setTestMethodAssignmentsForRequirement: (requirementId: string, assignments: RequirementTestMethodAssignmentParams[]) => void;
  setRequirmentMilestoneAssignments: (assignments: RequirementMilestoneAssignmentParams[]) => void;
  setRequirementSampleMaturityAssignments: (assignments: RequirementSampleMaturityAssignmentParams[]) => void;
  fetchVerificationCriteria: (requirementId: string) => Promise<void>;
  fetchAndSortVerificationCriteria: (requirementId: string) => Promise<void>;
  createVerificationCriteria: (data: CreateVerificationCriteriaRequest) => Promise<VerificationCriteria | undefined>;
  updateVerificationCriteria: (data: UpdateVerificationCriteriaRequest) => Promise<void>;
  deleteVerificationCriteria: (id: string) => Promise<void>;
  fetchVerificationCriteriaTasksByRequirementId: (requirementId: string) => Promise<void>;
  fetchReadyVerificationCriteriaTasksByProjectId: (projectId: string) => Promise<VerificationCriteriaTask[]>;
  createVerificationCriteriaTasks: (verificationCriteriaId: string, tasks: VerificationCriteriaTaskCreateInput[]) => Promise<void>;
  updateVerificationCriteriaTask: (task: VerificationCriteriaTask) => Promise<void>;
  updateVerificationCriteriaTasks: (tasks: VerificationCriteriaTaskUpdateInput[]) => Promise<void>;
  deleteVerificationCriteriaTasks: (ids: string[]) => Promise<void>;
  fetchRequirement: (id: string) => Promise<void>;
  fetchRequirementsBySubject: (subjectId: string, subjectType: RequirementSubjectType) => Promise<void>;
  fetchOrganizationRequirements: (propelOrgId: string) => Promise<void>;
  fetchAssignmentsByRequirementId: (requirementId: string) => Promise<void>;
  fetchAllAssignmentsForProject: (projectId: string) => Promise<void>;
  fetchTestMethodAssignmentsByRequirementId: (requirementId: string) => Promise<void>;
  fetchRequirmentMilestoneAssignments: (requirementId: string) => Promise<void>;
  fetchRequirementSampleMaturityAssignments: (requirementId: string) => Promise<void>;
  createRequirement: (requirement: CreateRequirementRequest) => Promise<void>;
  updateRequirement: (id: string, data: Partial<CreateRequirementRequest>) => Promise<boolean>;
  updateAssignedSupplierUserId: (id: string, assignedSupplierUserId: string | null) => Promise<void>;
  deleteRequirement: (id: string) => Promise<boolean>;
  createAssignment: (requirementId: string, procedureInstanceId: string) => Promise<boolean>;
  deleteAssignment: (requirementId: string, assignmentId: string) => Promise<boolean>;
  removeAssignmentsForProcedure: (procedureInstanceId: string) => void;
  createTestMethodAssignment: (requirementId: string, testMethod: TestMethod) => Promise<boolean>;
  deleteTestMethodAssignment: (requirementId: string, assignmentId: string) => Promise<boolean>;
  createRequirmentMilestoneAssignment: (requirementId: string, milestoneId: string) => Promise<void>;
  deleteRequirmentMilestoneAssignment: (requirementId: string, assignmentId: string) => Promise<void>;
  createRequirementSampleMaturityAssignment: (requirementId: string, sampleMaturityId: string) => Promise<void>;
  deleteRequirementSampleMaturityAssignment: (requirementId: string, assignmentId: string) => Promise<void>;
  reset: () => void;
  assignRequirements: (projectId: string, requirementIds: string[]) => Promise<void>;
}

const initialState = {
  requirements: [],
  organizationRequirements: [],
  assignmentsByRequirement: {},
  testMethodAssignmentsByRequirement: {},
  requirmentMilestoneAssignments: [],
  requirementSampleMaturityAssignments: [],
  verificationCriteria: [],
  verificationCriteriaTasks: [],
};

export const useRequirementsStore = create<RequirementsStore>((set, get) => ({
  ...initialState,
  setRequirements: (requirements) => {
    set({ requirements: requirements.map(requirement => new Requirement(requirement)) });
  },
  setOrganizationRequirements: (requirements) => {
    set({ organizationRequirements: requirements });
  },
  setAssignmentsForRequirement: (requirementId, assignments) => {
    set({ 
      assignmentsByRequirement: {
        ...get().assignmentsByRequirement,
        [requirementId]: assignments
      }
    });
  },
  setTestMethodAssignmentsForRequirement: (requirementId, assignments) => {
    set({ 
      testMethodAssignmentsByRequirement: {
        ...get().testMethodAssignmentsByRequirement,
        [requirementId]: assignments
      }
    });
  },
  setRequirmentMilestoneAssignments: (assignments) => {
    set({ requirmentMilestoneAssignments: assignments.map(assignment => new RequirementMilestoneAssignment(assignment)) });
  },
  setRequirementSampleMaturityAssignments: (assignments) => {
    set({ requirementSampleMaturityAssignments: assignments.map(assignment => new RequirementSampleMaturityAssignment(assignment)) });
  },
  fetchRequirement: async (id: string) => {
    const response = await new RequirementFrontendController().getRequirement(id);
    if (response && response.status === 200 && response.requirement) {
      get().setRequirements([response.requirement]);
    }
  },
  fetchRequirementsBySubject: async (subjectId: string, subjectType: RequirementSubjectType) => {
    const controller = new RequirementFrontendController();
    const response = await controller.getRequirementsBySubject(subjectId, subjectType);
    if (response && response.status === 200 && response.requirements) {
      if (subjectType === RequirementSubjectType.FUSA_ORGANIZATION) {
        get().setOrganizationRequirements(response.requirements);
      } else {
        get().setRequirements(response.requirements);
      }
    }
  },
  fetchOrganizationRequirements: async (propelOrgId: string) => {
    const controller = new RequirementFrontendController();
    const response = await controller.getRequirementsBySubject(propelOrgId, RequirementSubjectType.FUSA_ORGANIZATION);
    if (response && response.status === 200 && response.requirements) {
      get().setOrganizationRequirements(response.requirements);
    }
  },
  fetchAssignmentsByRequirementId: async (requirementId: string) => {
    const controller = new RequirementProcedureTemplateAssignmentFrontendController();
    const response = await controller.getAssignmentsByRequirementId(requirementId);
    if (response && response.status === 200 && response.assignments) {
      get().setAssignmentsForRequirement(requirementId, response.assignments);
    }
  },
  fetchAllAssignmentsForProject: async (projectId: string) => {
    const controller = new RequirementProcedureTemplateAssignmentFrontendController();
    const response = await controller.getAllForProject(projectId);
    if (response && response.status === 200 && response.assignments) {
      const newMap: { [requirementId: string]: RequirementProcedureTemplateAssignmentParams[] } = {};
      response.assignments.forEach(assignment => {
        if(newMap[assignment.requirementId]) {
          newMap[assignment.requirementId].push(assignment);
        } else {
          newMap[assignment.requirementId] = [assignment];
        }
      });
      set({ 
        assignmentsByRequirement: {
          ...get().assignmentsByRequirement,
          ...newMap
        }
      });
    }
  },
  fetchTestMethodAssignmentsByRequirementId: async (requirementId: string) => {
    const controller = new RequirementTestMethodAssignmentFrontendController();
    const response = await controller.getAssignmentsByRequirementId(requirementId);
    if (response && response.status === 200 && response.assignments) {
      get().setTestMethodAssignmentsForRequirement(requirementId, response.assignments);
    }
  },
  createRequirement: async (requirement: CreateRequirementRequest) => {
    const controller = new RequirementFrontendController();
    const response = await controller.createRequirement(requirement);
    if (response && response.status === 201 && response.requirement) {
      if (response.requirement.subjectType === RequirementSubjectType.FUSA_ORGANIZATION) {
        get().setOrganizationRequirements([response.requirement, ...get().organizationRequirements]);
      } else {
        get().setRequirements([response.requirement, ...get().requirements]);
      }
    }
  },
  updateRequirement: async (id: string, data: Partial<CreateRequirementRequest>) => {
    const controller = new RequirementFrontendController();
    const response = await controller.updateRequirement(id, data);
    if (response && response.status === 200 && response.requirement) {
      const requirements = get().requirements.map(r => 
        r.id === id ? response.requirement! : r
      );
      get().setRequirements(requirements);
      return true;
    }
    return false;
  },
  updateAssignedSupplierUserId: async (id: string, assignedSupplierUserId: string | null) => {
    const response = await new RequirementFrontendController().updateAssignedSupplierUserId(id, assignedSupplierUserId);
    const requirement = response.requirement;
    if (response && response.status === 200 && requirement) {
      const requirements = get().requirements.map(r =>
        r.id === id ? requirement : r
      );
      get().setRequirements(requirements);
    }
  },
  deleteRequirement: async (id: string) => {
    const controller = new RequirementFrontendController();
    const response = await controller.deleteRequirement(id);
    if (response && response.status === 200) {
      const requirements = get().requirements.filter(r => r.id !== id);
      get().setRequirements(requirements);
      return true;
    }
    return false;
  },
  createAssignment: async (requirementId: string, procedureInstanceId: string) => {
    const controller = new RequirementProcedureTemplateAssignmentFrontendController();
    const response = await controller.createAssignment({ requirementId, procedureInstanceId });
    if (response && response.status === 201 && response.assignment) {
      const currentAssignments = get().assignmentsByRequirement[requirementId] || [];
      get().setAssignmentsForRequirement(requirementId, [...currentAssignments, response.assignment]);
      return true;
    }
    return false;
  },
  deleteAssignment: async (requirementId: string, assignmentId: string) => {
    const controller = new RequirementProcedureTemplateAssignmentFrontendController();
    const response = await controller.deleteAssignment(assignmentId);
    if (response && response.status === 200) {
      const currentAssignments = get().assignmentsByRequirement[requirementId] || [];
      get().setAssignmentsForRequirement(requirementId, currentAssignments.filter(a => a.id !== assignmentId));
      return true;
    }
    return false;
  },
  // Deleting a procedure instance cascades its requirement assignments away in
  // the database, so drop the mirrored rows here too. Without this the assigned
  // procedures view keeps rendering assignments pointing at a deleted procedure.
  removeAssignmentsForProcedure: (procedureInstanceId: string) => {
    const assignmentsByRequirement = get().assignmentsByRequirement;
    const pruned: Record<string, RequirementProcedureTemplateAssignmentParams[]> = {};
    let changed = false;

    for (const [requirementId, assignments] of Object.entries(assignmentsByRequirement)) {
      const remaining = assignments.filter(a => a.procedureInstanceId !== procedureInstanceId);
      if (remaining.length !== assignments.length) {
        changed = true;
      }
      pruned[requirementId] = remaining;
    }

    if (changed) {
      set({ assignmentsByRequirement: pruned });
    }
  },
  createTestMethodAssignment: async (requirementId: string, testMethod: TestMethod) => {
    const controller = new RequirementTestMethodAssignmentFrontendController();
    const response = await controller.createAssignment(requirementId, { testMethod });
    if (response && response.status === 201 && response.assignment) {
      const currentAssignments = get().testMethodAssignmentsByRequirement[requirementId] || [];
      get().setTestMethodAssignmentsForRequirement(requirementId, [...currentAssignments, response.assignment]);
      return true;
    }
    return false;
  },
  deleteTestMethodAssignment: async (requirementId: string, assignmentId: string) => {
    const controller = new RequirementTestMethodAssignmentFrontendController();
    const response = await controller.deleteAssignment(requirementId, assignmentId);
    if (response && response.status === 200) {
      const currentAssignments = get().testMethodAssignmentsByRequirement[requirementId] || [];
      get().setTestMethodAssignmentsForRequirement(requirementId, currentAssignments.filter(a => a.id !== assignmentId));
      return true;
    }
    return false;
  },
  fetchRequirmentMilestoneAssignments: async (requirementId: string) => {
    const controller = new RequirementMilestoneAssignmentFrontendController();
    const response = await controller.readAllByRequirementId(requirementId);
    if (response && response.status === 200 && response.requirementMilestoneAssignments) {
      get().setRequirmentMilestoneAssignments(response.requirementMilestoneAssignments);
    }
  },
  createRequirmentMilestoneAssignment: async (requirementId: string, milestoneId: string) => {
    const controller = new RequirementMilestoneAssignmentFrontendController();
    const response = await controller.create({ requirementId, milestoneId });
    if (response && response.status === 201 && response.requirementMilestoneAssignment) {
      get().setRequirmentMilestoneAssignments([...get().requirmentMilestoneAssignments, response.requirementMilestoneAssignment]);
    }
  },
  deleteRequirmentMilestoneAssignment: async (requirementId: string, assignmentId: string) => {
    const controller = new RequirementMilestoneAssignmentFrontendController();
    const response = await controller.delete(assignmentId);
    if (response && response.status === 200) {
      get().setRequirmentMilestoneAssignments(get().requirmentMilestoneAssignments.filter(a => a.id !== assignmentId));
    }
  },
  fetchRequirementSampleMaturityAssignments: async (requirementId: string) => {
    const controller = new RequirementSampleMaturityAssignmentFrontendController();
    const response = await controller.readAllByRequirementId(requirementId);
    if (response && response.status === 200 && response.requirementSampleMaturityAssignments) {
      get().setRequirementSampleMaturityAssignments(response.requirementSampleMaturityAssignments);
    }
  },
  createRequirementSampleMaturityAssignment: async (requirementId: string, sampleMaturityId: string) => {
    const controller = new RequirementSampleMaturityAssignmentFrontendController();
    const response = await controller.create({ requirementId, sampleMaturityId });
    if (response && response.status === 201 && response.requirementSampleMaturityAssignment) {
      get().setRequirementSampleMaturityAssignments([...get().requirementSampleMaturityAssignments, response.requirementSampleMaturityAssignment]);
    }
  },
  deleteRequirementSampleMaturityAssignment: async (requirementId: string, assignmentId: string) => {
    const controller = new RequirementSampleMaturityAssignmentFrontendController();
    const response = await controller.delete(assignmentId);
    if (response && response.status === 200) {
      get().setRequirementSampleMaturityAssignments(get().requirementSampleMaturityAssignments.filter(a => a.id !== assignmentId));
    }
  },
  fetchVerificationCriteria: async (requirementId) => {
    const response = await new VerificationCriteriaFrontendController().readAllByRequirementId(requirementId);
    if (response && response.status === 200 && response.verificationCriteria) {
      set({ verificationCriteria: response.verificationCriteria.map(vc => new VerificationCriteria(vc)) });
    }
  },
  fetchAndSortVerificationCriteria: async (requirementId) => {
    const response = await new VerificationCriteriaFrontendController().readAllByRequirementId(requirementId);
    if (response && response.status === 200 && response.verificationCriteria) {
      set({ verificationCriteria: response.verificationCriteria.map(vc => new VerificationCriteria(vc)).sort((a, b) => STATUS_SORT_ORDER[a.status] - STATUS_SORT_ORDER[b.status]) });
    }
  },
  createVerificationCriteria: async (data) => {
    const response = await new VerificationCriteriaFrontendController().create(data);
    if (response && response.status === 201 && response.verificationCriteria) {
      const created = new VerificationCriteria(response.verificationCriteria);
      set({ verificationCriteria: [...get().verificationCriteria, created] });
      return created;
    }
    return undefined;
  },
  updateVerificationCriteria: async (data) => {
    const response = await new VerificationCriteriaFrontendController().update(data);
    const updatedCriteria = response.verificationCriteria;
    if (response && response.status === 200 && updatedCriteria) {
      set({
        verificationCriteria: get().verificationCriteria.map(vc =>
          vc.id === data.id ? new VerificationCriteria(updatedCriteria) : vc
        ),
      });
    }
  },
  deleteVerificationCriteria: async (id) => {
    const response = await  new VerificationCriteriaFrontendController().delete(id);
    if (response && response.status === 200) {
      set({ verificationCriteria: get().verificationCriteria.filter(vc => vc.id !== id) });
    }
  },
  fetchVerificationCriteriaTasksByRequirementId: async (requirementId) => {
    const response = await new VerificationCriteriaTaskController().readByRequirementId(requirementId);
    if (response && response.status === 200 && response.verificationCriteriaTasks) {
      set({ verificationCriteriaTasks: response.verificationCriteriaTasks.map(t => new VerificationCriteriaTask(t)) });
    }
  },
  fetchReadyVerificationCriteriaTasksByProjectId: async (projectId) => {
    const response = await new VerificationCriteriaTaskController().readReadyByProjectId(projectId);
    if (response && response.status === 200 && response.verificationCriteriaTasks) {
      const tasks = response.verificationCriteriaTasks.map(t => new VerificationCriteriaTask(t));
      set({ verificationCriteriaTasks: tasks });
      return tasks;
    }
    return [];
  },
  createVerificationCriteriaTasks: async (verificationCriteriaId, tasks) => {
    if (tasks.length === 0) return;
    const response = await new VerificationCriteriaTaskController().createMany({ verificationCriteriaId, tasks });
    if (response && response.status === 201 && response.verificationCriteriaTasks) {
      const created = response.verificationCriteriaTasks.map(t => new VerificationCriteriaTask(t));
      set({ verificationCriteriaTasks: [...get().verificationCriteriaTasks, ...created] });
    }
  },
  updateVerificationCriteriaTask: async (task) => {
    const response = await new VerificationCriteriaTaskController().update(task);
    if (response && response.status === 200 && response.verificationCriteriaTask) {
      const updated = new VerificationCriteriaTask(response.verificationCriteriaTask);
      set({ verificationCriteriaTasks: get().verificationCriteriaTasks.map(t => (t.id === updated.id ? updated : t)) });
    }
  },
  updateVerificationCriteriaTasks: async (tasks) => {
    if (tasks.length === 0) return;
    const response = await new VerificationCriteriaTaskController().updateMany({ tasks });
    if (response && response.status === 200 && response.verificationCriteriaTasks) {
      const updatedById = new Map(response.verificationCriteriaTasks.map(t => [t.id, new VerificationCriteriaTask(t)]));
      set({
        verificationCriteriaTasks: get().verificationCriteriaTasks.map(t => updatedById.get(t.id) ?? t),
      });
    }
  },
  deleteVerificationCriteriaTasks: async (ids) => {
    if (ids.length === 0) return;
    const response = await new VerificationCriteriaTaskController().deleteMany(ids);
    if (response && response.status === 200) {
      const idSet = new Set(ids);
      set({ verificationCriteriaTasks: get().verificationCriteriaTasks.filter(t => !idSet.has(t.id)) });
    }
  },
  reset: () => {
    set(initialState);
  },
  assignRequirements: async (projectId: string, requirementIds: string[]) => {
    const response = await new ProjectFrontendController().assignRequirements(projectId, requirementIds);
    if (response.status === 201) {
      const currentRequirements = get().requirements;
      get().setRequirements([...currentRequirements, ...response.requirements]);
    }
  },
}));
